import { useEffect, useState } from 'react'

import clsx from 'clsx'
import { motion } from 'framer-motion'

interface LevitatingStoneProps {
  initialAnimation: boolean
  onAnimationComplete?: () => void
  className?: string
}

export const ZenLevitatingStones = ({ initialAnimation, onAnimationComplete, className }: LevitatingStoneProps) => {
  const [isInitialAnimationComplete, setIsInitialAnimationComplete] = useState(false)

  useEffect(() => {
    if (!initialAnimation) {
      setIsInitialAnimationComplete(true)
    } else {
      // Reset state when initial animation starts again
      setIsInitialAnimationComplete(false)
    }
  }, [initialAnimation])

  const handleAnimationComplete = () => {
    setIsInitialAnimationComplete(true)
    if (onAnimationComplete) {
      onAnimationComplete()
    }
  }

  // Initial animation variants
  const initialVariants = {
    hidden: {
      opacity: 0,
      scale: 0.9,
      filter: 'blur(30px)',
    },
    visible: {
      opacity: 1,
      scale: 1,
      filter: 'blur(0px)',
      transition: {
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  }

  return (
    <div className={clsx('relative w-32 h-32 flex items-center justify-center', className)}>
      <motion.svg
        width='138'
        height='206'
        viewBox='0 0 138 206'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        style={{ transform: 'scale(0.6)', position: 'relative' }}
        initial={initialAnimation ? 'hidden' : { opacity: 1, scale: 1 }}
        animate={initialAnimation ? 'visible' : { opacity: 1, scale: 1 }}
        variants={initialVariants}
        onAnimationComplete={handleAnimationComplete}
      >
        <motion.g
          id='shadow'
          opacity='0.3'
          filter='url(#filter0_f_228_19170)'
          animate={
            isInitialAnimationComplete
              ? {
                  y: [0, 5],
                  scale: [1, 0.98],
                }
              : {}
          }
          transition={{
            y: {
              duration: 2.8,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'reverse',
              ease: 'easeInOut',
              delay: 0.5,
            },
            scale: {
              duration: 4.2,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'reverse',
              ease: 'easeInOut',
              delay: 0.7,
            },
          }}
        >
          <ellipse cx='67.5' cy='172.5' rx='57.5' ry='7.5' fill='#B96A39'></ellipse>
        </motion.g>

        <motion.path
          id='topRock'
          d='M26.3346 159.416L26.3298 159.411L26.3245 159.405C23.7442 156.935 21.7442 153.748 20.5116 150.399C18.5805 145.149 18.7734 138.536 21.1258 133.455L20.9546 133.376L21.1258 133.455C24.8798 125.346 32.5279 120.139 40.7249 117.129L40.7288 117.128L40.7529 117.121L40.8487 117.091C40.9335 117.065 41.0593 117.027 41.2234 116.978C41.5516 116.881 42.0326 116.741 42.6429 116.575C43.8636 116.242 45.6012 115.799 47.6677 115.36C51.8025 114.482 57.2462 113.622 62.4976 113.689C68.001 113.76 76.2351 114.874 83.1026 115.973C86.5343 116.522 89.6214 117.067 91.8506 117.475C92.9652 117.678 93.8652 117.848 94.4865 117.966C94.7972 118.026 95.0381 118.072 95.2014 118.104L95.387 118.14L95.4336 118.149C97.3732 118.906 99.2783 119.692 101.101 120.683C106.902 123.839 112.036 128.796 114.697 134.884L114.871 134.808L114.697 134.884C116.95 140.037 117.279 145.772 115.199 151.025C113.9 154.303 111.778 157.253 109.29 159.753C98.5345 170.566 82.309 173.562 67.5032 173.311C52.8446 173.062 36.0833 170.309 26.3346 159.416Z'
          fill='url(#paint0_linear_228_19170)'
          stroke='#F36111'
          strokeWidth='0.378676'
          animate={
            isInitialAnimationComplete
              ? {
                  y: [0, -8],
                  rotate: [0, 0.5],
                  scale: [1, 1.01],
                }
              : {}
          }
          transition={{
            y: {
              duration: 3,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'reverse',
              ease: 'easeInOut',
            },
            rotate: {
              duration: 3,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'reverse',
              ease: 'easeInOut',
            },
            scale: {
              duration: 4,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'reverse',
              ease: 'easeInOut',
            },
          }}
        />

        <motion.path
          id='middleRock'
          d='M80.4359 72.5228C90.511 75.1142 98.0993 78.4768 103.429 85.791C106.639 90.1963 108.366 95.7354 107.505 101.158C106.593 106.904 102.83 111.686 98.1828 115.037C97.2911 115.681 96.3542 116.25 95.3954 116.8C93.7121 117.684 92.036 118.496 90.2568 119.149C76.9166 124.046 59.6759 122.86 46.3602 118.164C44.4349 117.485 42.6072 116.641 40.7458 115.78C40.1591 115.486 39.5771 115.156 39.0143 114.813L38.916 114.975L39.0143 114.813C34.1471 111.854 29.7991 107.197 28.4368 101.571C27.1238 96.1483 28.4255 90.4302 31.3174 85.7209C35.4964 78.9156 41.3923 75.0925 49.9548 72.5414C59.5198 69.6917 67.4847 69.1916 80.4359 72.5228Z'
          fill='url(#paint1_linear_228_19170)'
          stroke='#FFAB0C'
          strokeWidth='0.378676'
          animate={
            isInitialAnimationComplete
              ? {
                  y: [0, -6],
                  rotate: [0, -0.3],
                  scale: [1, 1.015],
                }
              : {}
          }
          transition={{
            y: {
              duration: 3.5,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'reverse',
              ease: 'easeInOut',
              delay: 0.2,
            },
            rotate: {
              duration: 3.5,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'reverse',
              ease: 'easeInOut',
              delay: 0.2,
            },
            scale: {
              duration: 3.7,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'reverse',
              ease: 'easeInOut',
              delay: 0.3,
            },
          }}
        />

        <motion.path
          id='bottomRock'
          d='M49.9748 72.1858C45.9217 70.3305 42.3046 67.5287 40.7179 63.2585C39.3874 59.6778 39.7544 55.6387 41.353 52.1958C43.9851 46.5278 49.2439 42.4165 55.0611 40.3115C62.483 37.626 71.0081 37.6298 78.1863 41.0226C83.2138 43.3989 87.6316 47.6674 89.5012 52.9575C90.7469 56.4822 90.6691 60.4065 89.0399 63.7844C87.2013 67.5967 84.0645 70.1239 80.4137 72.161C80.0904 72.2367 79.7642 72.3482 79.4467 72.4647C79.3394 72.5041 79.2337 72.5437 79.1293 72.5829C78.9122 72.6644 78.7006 72.7438 78.4913 72.8141L78.5515 72.9936L78.4913 72.8141C77.354 73.196 76.214 73.5261 75.043 73.7698C67.4777 75.3447 57.1755 75.0605 49.9748 72.1858Z'
          fill='url(#paint2_linear_228_19170)'
          stroke='#FFBA18'
          strokeWidth='0.378676'
          animate={
            isInitialAnimationComplete
              ? {
                  y: [0, -10],
                  rotate: [0, 0.7],
                  scale: [1, 1.02],
                }
              : {}
          }
          transition={{
            y: {
              duration: 2.8,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'reverse',
              ease: 'easeInOut',
              delay: 0.5,
            },
            rotate: {
              duration: 2.8,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'reverse',
              ease: 'easeInOut',
              delay: 0.5,
            },
            scale: {
              duration: 4.2,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'reverse',
              ease: 'easeInOut',
              delay: 0.7,
            },
          }}
        />

        <motion.g
          id='lines'
          animate={
            isInitialAnimationComplete
              ? {
                  y: [0, -10],
                  rotate: [0, 0.7],
                  scale: [1, 1.02],
                }
              : {}
          }
          transition={{
            y: {
              duration: 2.8,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'reverse',
              ease: 'easeInOut',
              delay: 0.5,
            },
            rotate: {
              duration: 2.8,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'reverse',
              ease: 'easeInOut',
              delay: 0.5,
            },
            scale: {
              duration: 4.2,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'reverse',
              ease: 'easeInOut',
              delay: 0.7,
            },
          }}
        >
          <path
            d='M110.044 30.4884C110.331 30.4786 110.641 30.4702 110.915 30.5671C111.334 30.7159 111.702 31.058 111.877 31.4684C112.09 31.9689 111.937 32.5923 111.75 33.0783C110.519 36.2824 101.013 44.9857 97.7798 46.4386C97.2746 46.5376 96.7795 46.5497 96.3132 46.2984C96.1148 46.1915 95.9789 46.0338 95.9201 45.8149C95.7464 45.1693 96.147 44.3125 96.44 43.7501C98.4988 39.7996 105.743 31.8426 110.044 30.4884Z'
            fill='#ED7123'
          ></path>
          <path
            d='M92.587 19.3251C92.7045 19.3206 92.822 19.3192 92.9395 19.3207C93.4539 19.3285 93.9241 19.4906 94.2778 19.8836C94.731 20.3869 94.8301 21.121 94.8018 21.7731C94.6402 25.496 90.6586 34.4666 87.9174 36.9514C87.7875 37.0247 87.655 37.099 87.5157 37.1535C87.2194 37.2696 86.8969 37.3221 86.5996 37.1835C86.3429 37.0639 86.1707 36.7896 86.0729 36.5341C85.1949 34.2413 88.0661 26.1248 89.0488 23.8391C89.7949 22.104 90.7399 20.0641 92.587 19.3251Z'
            fill='#ED7123'
          ></path>
          <path
            d='M114.062 50.7381C114.087 50.735 114.112 50.7309 114.137 50.729C115.25 50.6457 117.065 50.5726 117.952 51.3553C118.299 51.6616 118.527 52.0939 118.548 52.559C118.576 53.1843 118.288 53.7817 117.861 54.2243C115.743 56.4195 108.135 56.7875 105.05 56.8528C103.852 56.8657 101.854 57.1012 100.869 56.3493C100.596 56.1412 100.37 55.7935 100.346 55.4454C100.322 55.1008 100.469 54.8335 100.688 54.5798C102.555 52.4126 111.218 50.9905 114.062 50.7381Z'
            fill='#ED7123'
          ></path>
        </motion.g>

        <defs>
          <filter
            id='filter0_f_228_19170'
            x='6'
            y='161'
            width='123'
            height='23'
            filterUnits='userSpaceOnUse'
            colorInterpolationFilters='sRGB'
          >
            <feFlood floodOpacity='0' result='BackgroundImageFix'></feFlood>
            <feBlend mode='normal' in='SourceGraphic' in2='BackgroundImageFix' result='shape'></feBlend>
            <feGaussianBlur stdDeviation='2' result='effect1_foregroundBlur_228_19170'></feGaussianBlur>
          </filter>
          <linearGradient
            id='paint0_linear_228_19170'
            x1='67.893'
            y1='116.95'
            x2='67.893'
            y2='173.514'
            gradientUnits='userSpaceOnUse'
          >
            <stop stopColor='#F17725'></stop>
            <stop offset='1' stopColor='#E56821'></stop>
          </linearGradient>
          <linearGradient
            id='paint1_linear_228_19170'
            x1='67.8249'
            y1='72.3394'
            x2='67.8249'
            y2='122.489'
            gradientUnits='userSpaceOnUse'
          >
            <stop stopColor='#FFBC28'></stop>
            <stop offset='1' stopColor='#FFB31C'></stop>
          </linearGradient>
          <linearGradient
            id='paint2_linear_228_19170'
            x1='65.1316'
            y1='38.1905'
            x2='65.1316'
            y2='74.9101'
            gradientUnits='userSpaceOnUse'
          >
            <stop stopColor='#FFD257'></stop>
            <stop offset='1' stopColor='#FFDE8A'></stop>
          </linearGradient>
        </defs>
      </motion.svg>
    </div>
  )
}
